import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Fab,
  Link,
  Skeleton,
  Stack,
  SvgIconProps,
  Typography,
} from '@mui/material'
import React, { FC, useEffect, useRef, useState } from 'react'
import YouTube from 'react-youtube'
import { Icons } from 'utoniq-ui'

import {
  Artist,
  ConsoleEventAggregationConnectionFragment,
  ConsoleHomeQuery,
  Maybe,
  MembershipSubscriptionProduct,
} from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsLeftProps,
  ArtistHeaderItemsRight,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ConsoleEventTicketInfoBox } from '@/components/organisms'
import { HorizontalList } from '@/components/organisms/functionals'
import { CreateItemActionSheet } from '@/components/pages/ConsoleItems/template'
import { CreatePostActionSheet } from '@/components/pages/ConsolePosts/template'
import { ArtistThemeProvider, styles, styleSets } from '@/components/styles'
import { config } from '@/config'
import { StorageImage, useCurrentUser, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { usePlayingResource } from '@/core/resources'
import { openIntercom, updateIntercom } from '@/lib/intercom'

export * from './fragments'

export type State = {
  loading: boolean
  artistId: string
  artist: ConsoleHomeQuery['artist']
  totalSales: string
  memberships: string
  videoId: string
  currentUser: Maybe<ArtistHeaderItemsLeftProps['currentUser']>
  campaign: Maybe<{
    note: string
    point: number
    content: string
    title: string
  }>
  tips: {
    itemCleared: boolean
    postCleared: boolean
    threadCleared: boolean
  }
  events: Maybe<ConsoleEventAggregationConnectionFragment>
  updatedAt?: string
  membership: Pick<MembershipSubscriptionProduct, 'productId'>[] | undefined
}

type CampaignAreaCellProps = {
  campaign: Exclude<State['campaign'], null>
}

type MovieAreaCellProps = {
  videoId: State['videoId']
}

export type Action = {}

export const ConsoleHomeTemplate: Template<State, Action> = ({ state }) => {
  const { routerNames, routerPages } = useRouter()
  const { t } = useTranslation('consoleHome')
  const { user } = useCurrentUser()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleDashboard} />
      <ConsoleBaseLayout
        hasConsoleFooter
        header={{
          left: (
            <ArtistHeaderItemsLeft
              currentUser={state.currentUser}
              paymentType={state.artist?.paymentType}
              hasArrangingDeliveryItems={
                state.artist?.hasArrangingDeliveryItems
              }
              membership={state.membership}
            />
          ),
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
        sx={{
          position: 'relative',
          height: '100vh',
        }}
        fullWidth={true}
      >
        {/**
         * @description
         * remove false when function is implemented in the future
         */}
        {!state.loading &&
          config.modules.membership &&
          !state.artist?.subscription &&
          state.artist?.paymentType === 'connect' && (
            <Stack sx={{ position: 'relative' }}>
              <Link href={routerPages.ConsoleMembershipExplain}>
                <StorageImage
                  style={{ width: '100%', height: 'auto' }}
                  target="common"
                  src="images/console/membership_banner.png"
                />
                <Button
                  sx={{
                    position: 'absolute',
                    right: 16,
                    bottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 13,
                    gap: 2,
                  }}
                >
                  <Box>{t('seeDetail')}</Box>
                  <Icons.RightArrow
                    fontSize="xSmall"
                    color="white"
                    sx={{ maxWidth: '10px' }}
                  />
                </Button>
              </Link>
            </Stack>
          )}
        {state.loading && (
          <Stack spacing={5} sx={{ pt: 4, px: 5 }}>
            <Skeleton variant="rectangular" width="100%" height={124} />
            <Skeleton variant="rectangular" width="100%" height={200} />
          </Stack>
        )}
        <Stack direction="row-reverse">
          <Stack
            sx={{
              position: 'fixed',
              bottom: 100,
              zIndex: 5,
              maxWidth: 600,
              margin: '0 auto',
              px: 4,
              pb: styles.safeAreaBottom,
            }}
          >
            <Fab
              size="medium"
              color="primary"
              onClick={() => {
                updateIntercom(user)
                openIntercom()
              }}
              sx={{ position: 'relative', right: 5 }}
            >
              <Icons.Inquiry fontSize="medium" sx={{ color: 'white' }} />
            </Fab>
          </Stack>
        </Stack>
        {!state.loading && state.artist && (
          <DataCell
            paymentType={state.artist.paymentType}
            followerCount={state.artist?.followerCount ?? 0}
            memberships={state.memberships}
            totalPurchasedNumber={state.artist?.totalPurchasedNumber ?? 0}
            showMembership={
              config.modules.membership && !!state.artist?.subscription
            }
          />
        )}
        {state.campaign && <CampaignAreaCell campaign={state.campaign} />}
        {!state.loading &&
          /**
           * utoniq or デジリアの場合、Tipsを表示
           */
          ['utoniq', 'digirea'].some(key => key === config.applicationKey) && (
            <TipsCell artistId={state.artistId} tips={state.tips} />
          )}
        {config.modules.event && (
          <TicketStatusCell
            loading={state.loading}
            events={state.events}
            updatedAt={state.updatedAt}
          />
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type DataCellProps = {
  memberships: string
  followerCount: number
  showMembership: boolean
  paymentType: Artist['paymentType']
  totalPurchasedNumber: number
}
const DataCell: Cell<DataCellProps> = ({
  memberships,
  paymentType,
  followerCount,
  showMembership,
  totalPurchasedNumber,
}) => {
  const { t } = useTranslation('consoleHome')
  const { routerPages } = useRouter()

  return (
    <CellFrame sx={{ py: 4, maxWidth: 600, margin: '0 auto' }}>
      <Stack direction="row" sx={{ py: 4 }}>
        {config.modules.store && (
          <>
            <Stack
              flex={1}
              spacing={1}
              sx={{ height: 48 }}
              justifyContent="center"
            >
              <Typography variant="h6" align="center">
                {totalPurchasedNumber}
              </Typography>
              <Typography
                variant="overline"
                color="text.secondary"
                align="center"
              >
                {t('itemSale')}
              </Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        {showMembership && (
          <>
            <Stack
              flex={1}
              spacing={1}
              sx={{ height: 48 }}
              justifyContent="center"
            >
              <Typography variant="h6" align="center">
                {memberships}
              </Typography>
              <Typography
                variant="overline"
                color="text.secondary"
                align="center"
              >
                {t('member')}
              </Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
          </>
        )}
        <Stack flex={1} spacing={1} sx={{ height: 48 }} justifyContent="center">
          <Typography variant="h6" align="center">
            {followerCount}
          </Typography>
          <Typography variant="overline" color="text.secondary" align="center">
            {t('follower')}
          </Typography>
        </Stack>
      </Stack>
    </CellFrame>
  )
}

{
  /**
   * @description
   * Open when function is implemented in the future
   * https://app.clickup.com/t/908127/UT-5043
   */
}

const MovieAreaCell: Cell<MovieAreaCellProps> = ({ videoId }) => {
  const { t } = useTranslation('consoleHome')
  const videoRef = useRef<YouTube>(null)
  const { playingResource, playResource } = usePlayingResource()

  useEffect(() => {
    if (playingResource !== videoId) {
      videoRef.current?.getInternalPlayer()?.pauseVideo()
    }
  }, [playingResource, videoId])

  return (
    <CellFrame sx={{ pt: 5, pb: 7, px: 5 }}>
      <Card
        sx={{
          bgcolor: 'grey.50',
          borderRadius: 2,
        }}
        elevation={0}
      >
        <CardContent sx={{ p: 5 }}>
          <Stack sx={{ mb: 1 }}>
            <Typography variant="h5" sx={{ mb: 4 }} fontWeight={6}>
              {t('howToUseUtoniq')}
            </Typography>
            {videoId && (
              <YouTube
                id={videoId}
                videoId={videoId}
                opts={{
                  width: '100%',
                  height: '168px',
                }}
                ref={videoRef}
                onPlay={() => playResource(videoId ?? '')}
              />
            )}
            <Stack sx={{ pt: 6 }} direction="row" justifyContent="center">
              <Button color="primary" sx={{ minWidth: 244 }} size="large">
                {t('makeProduct')}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}
const CampaignAreaCell: Cell<CampaignAreaCellProps> = ({ campaign }) => {
  const { t } = useTranslation('consoleHome')

  return (
    <CellFrame sx={{ pt: 5, pb: 7, px: 5 }}>
      <Card
        sx={{
          bgcolor: 'grey.50',
          borderRadius: 2,
        }}
        elevation={0}
      >
        <CardContent sx={{ p: 5 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 5 }}>
            <Icons.Campaign sx={{ color: 'secondary.main' }} />
            <Typography variant="h4">{t('duringCampaignEntry')}</Typography>
          </Stack>
          <Stack spacing={2} sx={{ mb: 5 }}>
            <Typography variant="h5">{campaign.title}</Typography>
            <Typography color="text.secondary" variant="body2">
              {campaign.note}
            </Typography>
          </Stack>
          <Stack spacing={2} sx={{ mb: 5 }}>
            <Typography variant="h5">
              {campaign.point}
              {t('point')}
            </Typography>
            <Typography variant="body2">{campaign.content}</Typography>
            <Link href="#" color="primary.main" variant="subtitle1">
              {t('clickHere')}
            </Link>
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}

type TipsCellProps = {
  artistId: State['artistId']
  tips: State['tips']
}
const TipsCell: Cell<TipsCellProps> = ({
  artistId,
  tips: { itemCleared, threadCleared, postCleared },
}) => {
  const { t } = useTranslation('consoleHome')
  const { routerPages, router, generatePath } = useRouter()
  const [openItemCreate, setOpenItemCreate] = useState<boolean>(false)
  const [openPostCreate, setOpenPostCreate] = useState<boolean>(false)

  const tips: TipCardProps[] = [
    {
      title: t('itemTipTitle'),
      content: t('itemTipContent'),
      isCleared: itemCleared,
      Icon: Icons.OwnedItems,
      isBe: config.modules.item,
      onClick() {
        setOpenItemCreate(true)
      },
    },
    {
      title: t('postTipTitle'),
      content: t('postTipContent'),
      isCleared: postCleared,
      Icon: Icons.Post,
      isBe: true,
      onClick() {
        setOpenPostCreate(true)
      },
    },
    {
      title: t('threadTipTitle'),
      content: t('threadTipContent'),
      isCleared: threadCleared,
      Icon: Icons.Thread,
      isBe: config.modules.thread,
      onClick() {
        router.push(generatePath(routerPages.ArtistThreadCreate, { artistId }))
      },
    },
  ]

  return (
    <CellFrame sx={{ mt: 4 }}>
      <Stack direction="row" px={5} spacing={1} alignItems="center">
        <Icons.Tips />
        <Typography variant="h4">{t('tips')}</Typography>
      </Stack>
      {/* 画面幅 md 以上のとき画面端で折り返し一覧表示する TIPS */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap={3}
        justifyContent="flex-start"
        sx={theme => ({
          pt: 2,
          pl: '20px',
          [theme.breakpoints.down('md')]: { display: 'none' },
        })}
      >
        {tips.map((tip, index) =>
          tip.isBe ? <TipCard key={index} {...tip} /> : null
        )}
      </Box>
      {/* 画面幅 md 未満のときスクロール表示する TIPS */}
      <Stack
        flex={1}
        flexWrap="wrap"
        sx={{
          pt: 2,
          overflowX: 'scroll',
          display: { md: 'none' },
          ...styleSets.hideScrollbar(),
        }}
      >
        <Stack
          direction="row"
          spacing={3}
          sx={{
            '& > *:first-of-type': {
              ml: 5,
            },
            '& > *:last-child': {
              mr: 5,
            },
          }}
        >
          {tips.map((tip, index) => {
            return tip.isBe ? <TipCard {...tip} key={index} /> : null
          })}
        </Stack>
      </Stack>

      <CreateItemActionSheet
        open={openItemCreate}
        onClose={() => setOpenItemCreate(false)}
        fullWidth={true}
      />
      <CreatePostActionSheet
        open={openPostCreate}
        onClose={() => setOpenPostCreate(false)}
        fullWidth={true}
      />
    </CellFrame>
  )
}

type TipCardProps = {
  title: string
  content: string
  Icon: (props: SvgIconProps) => JSX.Element
  isBe: boolean
  onClick?: () => void
  isCleared: boolean
}
const TipCard: FC<TipCardProps> = ({
  isCleared,
  title,
  content,
  Icon,
  onClick,
}) => {
  const { t } = useTranslation('consoleHome')
  return (
    <Card sx={{ width: 152, borderRadius: 3 }} elevation={0}>
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Stack spacing={1}>
          <Box sx={{ position: 'relative' }}>
            <Icon sx={{ color: isCleared ? 'text.hint' : 'text.primary' }} />
            {isCleared && (
              <Stack
                sx={{
                  position: 'absolute',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: 'secondary.main',
                  top: 0,
                  left: 0,
                }}
                justifyContent="center"
                alignItems="center"
              >
                <Icons.Check sx={{ color: 'white', width: 8, height: 8 }} />
              </Stack>
            )}
          </Box>
          <Typography
            variant="subtitle1"
            color={isCleared ? 'text.hint' : 'text.primary'}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            color={isCleared ? 'text.hint' : 'text.secondary'}
          >
            {content}
          </Typography>
        </Stack>
        <Button
          sx={{ height: 24, mt: 2, borderRadius: 1 }}
          fullWidth
          color="secondary"
          disabled={isCleared}
          onClick={onClick}
        >
          <Typography variant="subtitle2" color="white">
            {isCleared ? t('clear') : t('create')}
          </Typography>
        </Button>
      </CardContent>
    </Card>
  )
}

type TicketStatusCellProps = {
  loading: State['loading']
  events: State['events']
  updatedAt: State['updatedAt']
}
const TicketStatusCell: Cell<TicketStatusCellProps> = ({
  loading,
  events,
  updatedAt,
}) => {
  const { t } = useTranslation('consoleHome')
  const { routerPages } = useRouter()

  return (
    <CellFrame>
      {/* 画面幅 md 以上で表示する TOP2 のイベントカード */}
      <HorizontalList
        sx={{ pt: 2, display: { xs: 'none', md: 'block' } }}
        loading={loading}
        allwayShowList
        loadingItemLength={3}
        spacing={3}
        title={t('ticketStatusLabel', 'チケット状況')}
        titleTypographyProps={{ variant: 'h4' }}
        transTypographyProps={{ variant: 'h6' }}
        listHref={routerPages.ConsoleShokufesEvents}
        subTitle={
          updatedAt ? t('subTicketStatusLabel', { date: updatedAt }) : undefined
        }
        subTitleTypographyProps={{
          sx: { width: '100%' },
          align: 'right',
          color: 'text.secondary',
        }}
        items={events?.edges.slice(0, 2) ?? []}
        trans={t('eventList', 'イベント一覧')}
        renderItem={({ node }, i) => (
          <ConsoleEventTicketInfoBox key={i} event={node} width={'calc(50%)'} />
        )}
        EmptyComponent={
          <Stack alignItems="center" justifyContent="center" p={5}>
            <Typography variant="body1" color="text.secondary">
              {t('emptyTicketMessage', 'チケット状況はありません')}
            </Typography>
          </Stack>
        }
      />
      {/* 画面幅 md 未満でスクロール表示するイベントカード */}
      <HorizontalList
        sx={{ pt: 2, display: { md: 'none' } }}
        loading={loading}
        allwayShowList
        loadingItemLength={3}
        spacing={3}
        title={t('ticketStatusLabel', 'チケット状況')}
        titleTypographyProps={{ variant: 'h4' }}
        transTypographyProps={{ variant: 'h6' }}
        listHref={routerPages.ConsoleShokufesEvents}
        subTitle={
          updatedAt ? t('subTicketStatusLabel', { date: updatedAt }) : undefined
        }
        subTitleTypographyProps={{
          sx: { width: '100%' },
          align: 'right',
          color: 'text.secondary',
        }}
        items={events?.edges ?? []}
        trans={t('eventList', 'イベント一覧')}
        renderItem={({ node }, i) => (
          <ConsoleEventTicketInfoBox key={i} event={node} width={316} />
        )}
        EmptyComponent={
          <Stack alignItems="center" justifyContent="center" p={5}>
            <Typography variant="body1" color="text.secondary">
              {t('emptyTicketMessage', 'チケット状況はありません')}
            </Typography>
          </Stack>
        }
      />
    </CellFrame>
  )
}
